<template>
  <div id="app">
    <v-style v-for="(style, index) in fileFontStyles" :key="`font-${index}`">{{
      style
    }}</v-style>

    <!--
    Even when routes use the same component, treat them
    as distinct and create the component again.
    -->
    <RouterView :key="$route.fullPath" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import get from 'lodash.get'
import appConfig from '@src/app.config'

export default {
  page: {
    // All subcomponent titles will be injected into this template.
    titleTemplate(title) {
      title = typeof title === 'function' ? title(this.$store) : title
      return title ? `${title} | ${appConfig.title}` : appConfig.title
    },
  },
  computed: {
    ...mapGetters({
      fonts: 'contentful/fonts',
    }),
    fileFontStyles() {
      const fileFonts = this.fonts.filter((fontObject) => {
        const fontType = get(fontObject, 'sys.contentType.sys.id')
        return fontType && fontType.includes('fileFont')
      })
      return fileFonts.map((font) => font.fields && font.fields.fontConfig)
    },
    typekitFontUrls() {
      const typekitFonts = this.fonts.filter((fontObject) => {
        const fontType = get(fontObject, 'sys.contentType.sys.id')
        return fontType && fontType.includes('typekitFont')
      })
      return typekitFonts.map(
        (font) => font.fields && font.fields.linkToStylesheet
      )
    },
  },
}
</script>

<!-- This should generally be the only global CSS in the app. -->
<style lang="scss">
// Allow element/type selectors, because this is global CSS.
// stylelint-disable selector-max-type, selector-class-pattern

// Normalize default styles across browsers,
// https://necolas.github.io/normalize.css/
@import '~normalize.css/normalize.css';
// Style loading bar between pages.
// https://github.com/rstacruz/nprogress
@import '~nprogress/nprogress.css';

// Design variables and utilities from src/design.
@import '@design';

@font-face {
  font-family: 'alte-haas-grotesk';
  src: url('~/assets/fonts/alte_haas_grotesk_regular.woff') format('woff');
  font-weight: 400;
  font-style: normal;
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

body {
  background: $color-body-bg;
}
#app {
  font-size: 16px;
  text-align: center;
}

// ===
// Base element styles
// ===

h1 {
  font-size: 22px;
  margin: 0 0 16px;
}

h2 {
  font-size: 18px;
}

p {
  margin: 0;
}

.toasted.default-toast.primary.default {
  align-items: center;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  flex-direction: column;
  font-family: 'alte-haas-grotesk', sans-serif;
  height: 90px;
  padding: 16px 16px 12px;
  position: relative;
  text-align: center;
  color: $color-white;
  @media (min-width: 768px) {
    display: none;
  }
  a.action.ripple.toast__button {
    align-items: center;
    background-color: rgba(239, 46, 43, 1);
    border-radius: 0;
    color: $color-white;
    cursor: pointer;
    display: flex;
    font-size: 1.125em;
    height: 48px;
    justify-content: center;
    line-height: normal;
    margin: 10px 0 0 0;
    opacity: 1;
    text-transform: lowercase;
    width: 199px;
    &:active {
      text-decoration: none;
    }
  }
  a.action.ripple.toast__icon {
    background-color: rgba(0, 0, 0, 0.85);
    border-radius: 200px;
    border: 1px solid rgba(255, 255, 255, 1);
    height: 24px;
    margin: 0;
    padding: 0;
    position: absolute;
    right: 16px;
    top: -13px;
    width: 26px;
    &:after,
    &:before {
      background-color: rgba(255, 255, 255, 1);
      content: '';
      height: 1px;
      position: absolute;
      right: 6px;
      top: 11px;
      transform: rotate(45deg);
      width: 12px;
    }
    &:before {
      transform: rotate(-45deg);
    }
  }
}

// ===
// Vendor
// ===

#nprogress .bar {
  background: $color-link-text;
}
</style>
